import { useEffect, useState, type RefObject } from "react";

/**
 * Which of `root`'s `[data-<attr>]` descendants the reader is looking at: the one whose box
 * contains, or is nearest to, a line `focus` of the way down the viewport. The attribute's
 * value is the index.
 *
 * Recomputed from the elements' positions on each scroll (one rAF per scroll burst), rather
 * than from IntersectionObserver crossings, because crossings can be missed on a fast scroll
 * and then a short step is skipped: the reader sees 1 become 3. Positions cannot skip. In a
 * test runner every box is at the origin and the first element stays active.
 */
export function useActiveIndex(root: RefObject<HTMLElement | null>, attr: string, focus = 0.42): number {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const element = root.current;
    if (element === null) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const line = window.innerHeight * focus;
      let best = -1;
      let nearest = Infinity;
      for (const item of element.querySelectorAll<HTMLElement>(`[data-${attr}]`)) {
        const box = item.getBoundingClientRect();
        const distance = box.top > line ? box.top - line : box.bottom < line ? line - box.bottom : 0;
        if (distance < nearest) {
          nearest = distance;
          best = Number(item.dataset[attr]);
        }
      }
      if (best >= 0) setActive(best);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(frame);
    };
  }, [root, attr, focus]);
  return active;
}
